import { useQuery } from 'requests'
import { useTheme } from '@nfinite/react-hubstairs/styled/theme'
import { useDebounce } from '@nfinite/react-hubstairs/hooks'
import { FILTERS } from 'components/Commons/PaginatedList/Search'

function cleanNullOrUndefinedFilters(item) {
  if (!item) return
  const copy = { ...item }
  for (const attr in copy) {
    if (copy[attr] === null || copy[attr] === undefined) {
      delete copy[attr]
    }
  }
  return copy
}

function getLimit(skipped = 0, isMobile, pagingConfig) {
  const arrayLimits = isMobile ? pagingConfig.mobile : pagingConfig.desktop
  let limitFound = 0
  let index = 0
  let accumulator = skipped

  while (index < arrayLimits.length) {
    const limit = arrayLimits[index]
    limitFound = limit
    accumulator = accumulator - limit
    if (accumulator < 0) {
      break
    }
    index++
  }

  return limitFound
}

function getDataFromQuery({ data, previousData }) {
  // We need to keep the facets while loading next pages
  if (!data) return { paging: previousData?.paginated?.paging, facets: previousData?.paginated?.facets }
  const { paginated } = data
  if (!paginated) {
    throw new Error('Query used in PaginatedList must return data of format { paginated: { items, paging }}')
  }
  const { items, paging, facets } = paginated
  return { items, paging, facets }
}

// In some cases such as the isGeneric filter on product list we want to save the filter as null,
// but sending a null or undefined filter to the api SHOULD BE AVOIDED
//
// In some other case we may want to convert a filter value to a value the backend can read
// this too SHOULD BE AVOIDED
export function formatFilters({ filters, formatOutboundFilter, debouncedSearchText }) {
  const copyFilters = { ...filters }
  copyFilters[FILTERS.SEARCH] = debouncedSearchText

  if (formatOutboundFilter) {
    for (const filterName in formatOutboundFilter) {
      const formatCallback = formatOutboundFilter[filterName]
      const value = filters[filterName]
      copyFilters[filterName] = formatCallback(value)
    }
  }

  return cleanNullOrUndefinedFilters(copyFilters)
}

export function usePaginatedQuery({
  query,
  filters,
  sort,
  options,
  pagingConfig = {
    limits: {
      mobile: [30, 30, 30, 30, 30, 60],
      desktop: [30, 30, 30, 30, 30, 60],
    },
  },
  formatOutboundFilter,
}) {
  const { isMobile } = useTheme({ watchMobile: true })

  const initialLimit = getLimit(0, isMobile, pagingConfig.limits)

  const searchText =
    filters[FILTERS.SEARCH] !== null && filters[FILTERS.SEARCH] !== undefined
      ? filters[FILTERS.SEARCH].toString()
      : filters[FILTERS.SEARCH]

  const [debouncedSearchText] = useDebounce(searchText, 500)
  let paginatedQuery = query
  let queryVariables

  if (query.query) {
    paginatedQuery = query.query
    queryVariables = query.variables
  }
  const { data, previousData, error, fetchMore, refetch, networkStatus, updateQuery, startPolling, stopPolling } =
    useQuery(paginatedQuery, {
      variables: {
        ...queryVariables,
        filters: formatFilters({ filters, debouncedSearchText, formatOutboundFilter }),
        sort,
        paging: { skip: 0, limit: initialLimit },
      },
      ...options,
    })

  const loading = networkStatus <= 3
  const refetching = networkStatus === 4

  const { items, paging, facets } = getDataFromQuery({ data, previousData })

  const nextLimit = getLimit(items?.length, isMobile, pagingConfig.limits)

  return {
    items,
    paging,
    facets,
    loading,
    nextLimit,
    error,
    fetchMore,
    refetch,
    refetching,
    networkStatus,
    updateQuery,
    startPolling,
    stopPolling,
  }
}
